import React, { useEffect, useMemo, useState } from "react";
import { useOrganizationVendors, usePrimaryOrganization } from "../../hooks";
import { hasAnswered, questionsWithVisibility, QuestionWithVisibility } from "../../surveys/survey";
import {
  SurveyAnswerFn,
  SurveyAnswers,
  useParsedSurveyAnswers,
  useSurvey,
} from "../../../common/hooks/api/useSurvey";
import clsx from "clsx";
import { BUSINESS_SURVEY_NAME, businessSurvey } from "../../surveys/businessSurvey";
import { PageLoading } from "../../components";
import {
  SurveyQuestion,
  SurveyQuestionProps,
} from "../../components/get-compliant/survey/SurveyQuestion";
import { HR_SURVEY_NAME, hrSurvey } from "../../surveys/hrSurvey";
import { FlagIcon } from "@heroicons/react/24/outline";
import { FlagIcon as FlagIconFilled, CheckCircleIcon } from "@heroicons/react/24/solid";
import {
  EEA_UK_SURVEY_NAME,
  eeaUkSurvey,
  HAS_DATA_PRIVACY_OFFICER_SLUG,
  HAS_DESIGNATED_REP_SLUG,
  hasDataPrivacyOfficer,
  hasDesignatedRepresentative,
  isDataPrivacyOfficerRequired,
} from "../../surveys/eeaUkSurvey";
import { filterVendorsForDataSourceSurveyQuestion } from "../../components/get-compliant/survey/custom/AddDataSourceQuestion";
import { Breadcrumbs, Button, SimpleCheckboxControlled } from "@truevault/aquilo";
import {
  PrivacyCenterSettingsProps,
  usePrivacyCenterSettings,
} from "../../components/superorg/settings/PrivacyCenterSettings";
import { useHistory } from "react-router";
import { DpoValues, EditDpoDialog } from "../../components/dialog/EditDpoDialog";
import { PrivacyCenterPipedaPrivacyOfficerRow } from "../../components/organisms/settings/rows/PrivacyCenterPipedaPrivacyOfficerRow";
import { EditEeaContactDialog, EeaContactValues } from "../../components/dialog/EEAContactDialog";
import { initialViewAndQuestionSlug } from "../../pages/surveys/BusinessSurveyAquiloPage";
import { OrganizationDto } from "../../../common/service/server/dto/OrganizationDto";
import { FinancialIncentivesQuestion } from "../get-compliant/survey/custom/FinancialIncentivesQuestion";
import { CONSUMER_INCENTIVES_SURVEY_NAME } from "../../surveys/consumerCollectionIncentivesSurvey";
import { CONSUMER_FINANCIAL_INCENTIVES_QUESTION } from "../../surveys/orgSettings";
import {
  SurveyCheckboxesQuestion,
  SurveyCheckboxesQuestionProps,
} from "../get-compliant/survey/SurveyCheckboxesQuestion";
import {
  CUSTOM_AUDIENCE_VENDOR_FEATURE_SLUG,
  customAudienceSeparateVendorQuestion,
} from "../../surveys/sharingSurvey";
import { Routes } from "../../root/routes";
import { SELLING_AND_SHARING_SURVEY_NAME } from "../../pages/get-compliant/data-recipients/shared";
import { useApiSnackbar } from "../../../common/service/resources/mutators";

export function SurveyAnswersReview({
  render,
  redirectRouteIfSurveyIncomplete = null,
  showAdditionalOrgSettings = false,
}: {
  render: (isSurveyComplete: boolean, pageBody: React.ReactNode) => React.ReactElement;
  redirectRouteIfSurveyIncomplete?: string; // If set, then the page will redirect to this route if the survey is incomplete on initial page load (this is because in Get Compliant we want to prevent the user from accessing the review page until they've completed the survey, if they e.g. try to navigate to the review page directly from the Settings screen in Get Compliant)
  showAdditionalOrgSettings?: boolean; // If set, then will also include survey questions for (1) `offer-consumer-incentives` question from the `consumer-collection-incentives-survey` survey and (2) `custom-audience-feature-separate-vendor` question from the sharing survey. This flag is needed because we want to show these settings in Stay Compliant, but not in Get Compliant
}) {
  const history = useHistory();

  const [org] = usePrimaryOrganization();

  const { settings: pcSettings, privacyCenter } = usePrivacyCenterSettings();

  const {
    answers: bizAnswers,
    setAnswer: setBizAnswer,
    flaggedForReviewSlugs: bizFlaggedForReviewSlugs,
  } = useSurvey(org?.id, BUSINESS_SURVEY_NAME);

  const {
    answers: eeaAnswers,
    setAnswer: setEeaAnswer,
    flaggedForReviewSlugs: eeaFlaggedForReviewSlugs,
  } = useSurvey(org?.id, EEA_UK_SURVEY_NAME);

  const {
    answers: hrAnswers,
    setAnswer: setHrAnswer,
    flaggedForReviewSlugs: hrFlaggedForReviewSlugs,
  } = useSurvey(org?.id, HR_SURVEY_NAME);

  const {
    answers: incentivesAnswers,
    setAnswer: setIncentivesAnswer,
    flaggedForReviewSlugs: incentivesFlaggedForReviewSlugs,
  } = useSurvey(org?.id, CONSUMER_INCENTIVES_SURVEY_NAME);
  const parsedIncentivesAnswers = useParsedSurveyAnswers(incentivesAnswers);

  const allFlaggedForReviewSlugs = useMemo(
    () => [
      ...bizFlaggedForReviewSlugs,
      ...hrFlaggedForReviewSlugs,
      ...eeaFlaggedForReviewSlugs,
      ...incentivesFlaggedForReviewSlugs,
    ],
    [
      bizFlaggedForReviewSlugs,
      hrFlaggedForReviewSlugs,
      eeaFlaggedForReviewSlugs,
      incentivesFlaggedForReviewSlugs,
    ],
  );

  // Dialogs for editing DPO and Designated Rep details
  const [isEditDpoDialogOpen, setIsEditDpoDialogOpen] = useState(false);
  const [isDesignatedRepDialogOpen, setIsDesignatedRepDialogOpen] = useState(false);

  // If the survey isn't complete, then redirect to the main survey flow (and only do this once on initial page load)
  const [didCheckForCompleteSurveyOnPageLoad, setDidCheckForCompleteSurveyOnPageLoad] =
    useState(false);
  useEffect(() => {
    if (
      !didCheckForCompleteSurveyOnPageLoad &&
      org &&
      privacyCenter &&
      bizAnswers &&
      incentivesAnswers &&
      eeaAnswers &&
      hrAnswers &&
      pcSettings
    ) {
      setDidCheckForCompleteSurveyOnPageLoad(true);

      let [initialView] = initialViewAndQuestionSlug(
        org,
        { bizAnswers, incentivesAnswers, hrAnswers, eeaAnswers },
        privacyCenter,
        pcSettings,
        allFlaggedForReviewSlugs,
      );

      if (initialView !== "finished-review-modal" && redirectRouteIfSurveyIncomplete) {
        history.push(redirectRouteIfSurveyIncomplete);
      }
    }
  }, [
    org,
    privacyCenter,
    history,
    bizAnswers,
    incentivesAnswers,
    eeaAnswers,
    hrAnswers,
    allFlaggedForReviewSlugs,
    pcSettings,
    didCheckForCompleteSurveyOnPageLoad,
    setDidCheckForCompleteSurveyOnPageLoad,
    redirectRouteIfSurveyIncomplete,
  ]);

  if (!org || !bizAnswers || !hrAnswers || !eeaAnswers) {
    return render(false, <PageLoading />);
  }

  const bizSurveyDefinition = businessSurvey(bizAnswers);
  const bizVisibleQuestions = questionsWithVisibility(bizSurveyDefinition, bizAnswers).filter(
    (q) => q.visible,
  );

  const eeaSurveyDefinition = eeaUkSurvey(eeaAnswers, privacyCenter, pcSettings);
  const eeaVisibleQuestions = questionsWithVisibility(eeaSurveyDefinition, eeaAnswers).filter(
    (q) => q.visible,
  );

  const hrSurveyDefinition = hrSurvey(hrAnswers, org?.featureGdpr);
  const hrVisibleQuestions = questionsWithVisibility(hrSurveyDefinition, hrAnswers).filter(
    (q) => q.visible,
  );

  const financialIncentiveAnswer =
    parsedIncentivesAnswers[CONSUMER_FINANCIAL_INCENTIVES_QUESTION.slug];

  const isDpoRequired = org?.featureGdpr && isDataPrivacyOfficerRequired(eeaAnswers, privacyCenter);
  const hasDpo = Boolean(pcSettings?.dpoName) && Boolean(pcSettings?.dpoEmail);
  const hasDesignatedRep = hasDesignatedRepresentative(pcSettings);

  let isBizSurveyComplete = bizVisibleQuestions.every((q) =>
    hasAnswered(bizAnswers, q, /* debug = */ false),
  );
  let isHrSurveyComplete = hrVisibleQuestions.every((q) =>
    hasAnswered(hrAnswers, q, /* debug = */ false),
  );

  let isFinancialIncentivesSectionComplete =
    financialIncentiveAnswer !== undefined && financialIncentiveAnswer !== null;

  let isEeaUkSurveyComplete =
    !org?.featureGdpr ||
    [...eeaVisibleQuestions].every((q) => {
      // DPO and Designated Rep completion is handled specially
      if (q.slug === HAS_DATA_PRIVACY_OFFICER_SLUG) {
        let dpoAnswer = eeaAnswers[q.slug];
        return isDpoRequired
          ? hasDpo || dpoAnswer === "false" || privacyCenter?.dpoOfficerDoesNotApply
          : true;
      }
      if (q.slug === HAS_DESIGNATED_REP_SLUG) {
        let designatedRepAnswer = eeaAnswers[q.slug];
        return (
          hasDesignatedRep ||
          designatedRepAnswer === "false" ||
          privacyCenter?.hasDesignatedRepTask ||
          privacyCenter?.eeaContactDoesNotApply
        );
      }
      return hasAnswered(eeaAnswers, q, /* debug = */ false);
    });

  let pageBody = (
    <>
      <div className="tw-boundary">
        <Breadcrumbs
          title="Business Profile"
          source="Data Map"
          sourceRoute={"#"}
          hideHomeIcon={true}
        />

        <div className="mt-[24px] mb-[50px] space-y-[24px]">
          <ReviewAnswersPane
            title="Data Collection Practices"
            questionsWithVisibility={bizVisibleQuestions}
            isSectionComplete={isBizSurveyComplete}
            answers={bizAnswers}
            setAnswer={setBizAnswer}
            initialIsCollapsed={false}
            flaggedForReviewSlugs={bizFlaggedForReviewSlugs}
            pcSettings={pcSettings}
            openDpoDialog={() => setIsEditDpoDialogOpen(true)}
            openDesignatedRepDialog={() => setIsDesignatedRepDialogOpen(true)}
            {...{ isDpoRequired, hasDpo, hasDesignatedRep }}
          />

          {org?.featureGdpr && (
            <ReviewAnswersPane
              title="European Data Practices"
              questionsWithVisibility={eeaVisibleQuestions}
              isSectionComplete={isEeaUkSurveyComplete}
              answers={eeaAnswers}
              setAnswer={setEeaAnswer}
              flaggedForReviewSlugs={eeaFlaggedForReviewSlugs}
              pcSettings={pcSettings}
              openDpoDialog={() => setIsEditDpoDialogOpen(true)}
              openDesignatedRepDialog={() => setIsDesignatedRepDialogOpen(true)}
              {...{ isDpoRequired, hasDpo, hasDesignatedRep }}
            />
          )}

          {org?.featurePipeda && (
            <CollapsiblePane title="Canadian Data Practices">
              <div className="p-[12px]">
                <PrivacyCenterPipedaPrivacyOfficerRow
                  organization={pcSettings.organization}
                  vertical
                  useNewDesign
                  dpoName={pcSettings.dpoName}
                  dpoEmail={pcSettings.dpoEmail}
                  dpoPhone={pcSettings.dpoPhone}
                  dpoAddress={pcSettings.dpoAddress}
                  pipedaOfficerName={pcSettings.pipedaOfficerName}
                  pipedaOfficerEmail={pcSettings.pipedaOfficerEmail}
                  pipedaOfficerPhone={pcSettings.pipedaOfficerPhone}
                  pipedaOfficerAddress={pcSettings.pipedaOfficerAddress}
                  handleFormSubmit={pcSettings.handleFormSubmit}
                />
              </div>
            </CollapsiblePane>
          )}

          <ReviewAnswersPane
            title="HR Practices"
            questionsWithVisibility={hrVisibleQuestions}
            isSectionComplete={isHrSurveyComplete}
            answers={hrAnswers}
            setAnswer={setHrAnswer}
            flaggedForReviewSlugs={hrFlaggedForReviewSlugs}
            pcSettings={pcSettings}
            openDpoDialog={() => setIsEditDpoDialogOpen(true)}
            openDesignatedRepDialog={() => setIsDesignatedRepDialogOpen(true)}
            {...{ isDpoRequired, hasDpo, hasDesignatedRep }}
          />

          <FinancialIncentivesPane
            org={org}
            isSectionComplete={isFinancialIncentivesSectionComplete}
            incentivesAnswers={incentivesAnswers}
            setIncentivesAnswer={setIncentivesAnswer}
            flaggedForReviewSlugs={incentivesFlaggedForReviewSlugs}
          />

          {showAdditionalOrgSettings && <AdditionalOrgSettingsPane org={org} />}
        </div>
      </div>

      <EditDpoDialog
        open={isEditDpoDialogOpen}
        onClose={() => setIsEditDpoDialogOpen(false)}
        onDone={async (values: DpoValues) => {
          await pcSettings.handleFormSubmit({
            dpoOfficerName: values.name,
            dpoOfficerEmail: values.email,
            dpoOfficerPhone: values.phone,
            dpoOfficerAddress: values.address,
          });
        }}
        values={{
          name: pcSettings?.dpoName,
          email: pcSettings?.dpoEmail,
          phone: pcSettings?.dpoPhone,
          address: pcSettings?.dpoAddress,
        }}
      />

      <EditEeaContactDialog
        open={isDesignatedRepDialogOpen}
        onClose={() => setIsDesignatedRepDialogOpen(false)}
        onDone={async (values: EeaContactValues) => {
          await pcSettings.handleFormSubmit({
            eeaContactName: values.eeaContactName,
            eeaContactAddress: values.eeaContactAddress,
            eeaContactEmail: values.eeaContactEmail,
            eeaContactPhone: values.eeaContactPhone,
          });

          await setEeaAnswer(HAS_DESIGNATED_REP_SLUG, "true");
        }}
        values={{
          eeaContactName: pcSettings.eeaContactName,
          eeaContactAddress: pcSettings?.eeaContactAddress,
          eeaContactEmail: pcSettings?.eeaContactEmail,
          eeaContactPhone: pcSettings?.eeaContactPhone,
        }}
      />
    </>
  );

  let isSurveyComplete =
    isBizSurveyComplete &&
    isHrSurveyComplete &&
    isEeaUkSurveyComplete &&
    isFinancialIncentivesSectionComplete;

  return <>{render(isSurveyComplete, pageBody)}</>;
}

function FinancialIncentivesPane({
  org,
  isSectionComplete,
  incentivesAnswers,
  setIncentivesAnswer,
  flaggedForReviewSlugs,
}: {
  org: OrganizationDto;
  isSectionComplete: boolean;
  incentivesAnswers: SurveyAnswers;
  setIncentivesAnswer: SurveyAnswerFn;
  flaggedForReviewSlugs: string[];
}) {
  const [isEditing, setIsEditing] = useState(false);

  const parsedIncentivesAnswers = useParsedSurveyAnswers(incentivesAnswers);

  if (!incentivesAnswers) {
    return null;
  }

  let financialIncentiveAnswer =
    parsedIncentivesAnswers[CONSUMER_FINANCIAL_INCENTIVES_QUESTION.slug];

  let isFlaggedForReview = (flaggedForReviewSlugs || []).includes(
    CONSUMER_FINANCIAL_INCENTIVES_QUESTION.slug,
  );

  return (
    <CollapsiblePane title="Financial Incentives" showIncompleteBadge={!isSectionComplete}>
      <div className="p-[16px] flex items-start justify-between">
        <div>
          <div className="text-[18px] font-medium text-neutral-800 mb-[6px]">
            {CONSUMER_FINANCIAL_INCENTIVES_QUESTION.question}
          </div>
          {isEditing && (
            <div className="my-[6px] text-neutral-700 text-[16px]">
              {CONSUMER_FINANCIAL_INCENTIVES_QUESTION.help}
            </div>
          )}
          {isEditing ? (
            <FinancialIncentivesQuestion
              org={org}
              question={CONSUMER_FINANCIAL_INCENTIVES_QUESTION}
              answer={financialIncentiveAnswer}
              setAnswer={async (_question, answer: string) => {
                await setIncentivesAnswer(
                  CONSUMER_FINANCIAL_INCENTIVES_QUESTION.slug,
                  answer,
                  CONSUMER_FINANCIAL_INCENTIVES_QUESTION.question,
                );
              }}
              enableTask={true}
              useNewDesign={true}
              onlyShowInlineAnswerButtons={true}
              hideStayCompliantLinks={true}
            />
          ) : isSectionComplete ? (
            <div className="text-[16px] text-primary-600 font-bold">
              {financialIncentiveAnswer ? "Yes" : "No"}
            </div>
          ) : (
            <IncompleteBadge />
          )}
        </div>
        <div className="flex items-center">
          <div
            className="text-[14px] font-medium text-neutral-600 cursor-pointer underline"
            onClick={() => setIsEditing((s) => !s)}
          >
            {isEditing ? "Cancel" : "Edit"}
          </div>
          <div
            className="ml-[12px] cursor-pointer"
            onClick={() => {
              setIncentivesAnswer(
                CONSUMER_FINANCIAL_INCENTIVES_QUESTION.slug,
                isSectionComplete ? financialIncentiveAnswer.toString() : "",
                CONSUMER_FINANCIAL_INCENTIVES_QUESTION.question,
                !isFlaggedForReview,
              );
            }}
          >
            {isFlaggedForReview ? (
              <FlagIconFilled className="w-[24px] h-[24px] text-primary-500" />
            ) : (
              <FlagIcon className="w-[24px] h-[24px] text-primary-500" />
            )}
          </div>
        </div>
      </div>
    </CollapsiblePane>
  );
}

function AdditionalOrgSettingsPane({ org }: { org: OrganizationDto }) {
  // Data fetching / hooks for custom audience question
  const [vendors] = useOrganizationVendors(org.id);
  const { answers: sellingSharingAnswers, setAnswer: setSellingSharingAnswer } = useSurvey(
    org.id,
    SELLING_AND_SHARING_SURVEY_NAME,
  );
  const customAudienceManagedVendorQuestion = customAudienceSeparateVendorQuestion(
    vendors,
    Routes.dataMap.root,
  );

  const { apiSuccess } = useApiSnackbar();

  if (!vendors || vendors?.length === 0 || !sellingSharingAnswers) {
    return null;
  }

  return (
    <>
      <CollapsiblePane title="Sharing - Custom Audience">
        <div className="p-[16px]">
          <SurveyCheckboxesQuestion
            key={CUSTOM_AUDIENCE_VENDOR_FEATURE_SLUG}
            question={customAudienceManagedVendorQuestion as SurveyCheckboxesQuestionProps}
            answer={sellingSharingAnswers[CUSTOM_AUDIENCE_VENDOR_FEATURE_SLUG]}
            onAnswer={async (answer: string) => {
              await setSellingSharingAnswer(CUSTOM_AUDIENCE_VENDOR_FEATURE_SLUG, answer);
              apiSuccess("Custom audience vendor updated");
            }}
            collapseTopSpacing={true}
          />
        </div>
      </CollapsiblePane>
    </>
  );
}

function ReviewAnswersPane({
  title,
  isSectionComplete,
  questionsWithVisibility,
  answers,
  setAnswer,
  flaggedForReviewSlugs,
  pcSettings,
  initialIsCollapsed = true,
  openDpoDialog,
  openDesignatedRepDialog,
  isDpoRequired,
  hasDpo,
  hasDesignatedRep,
}: {
  title: string;
  isSectionComplete: boolean;
  questionsWithVisibility: QuestionWithVisibility[];
  answers: SurveyAnswers;
  setAnswer: SurveyAnswerFn;
  flaggedForReviewSlugs: string[];
  pcSettings: PrivacyCenterSettingsProps;
  initialIsCollapsed?: boolean;
  openDpoDialog: () => void;
  openDesignatedRepDialog: () => void;
  isDpoRequired: boolean;
  hasDpo: boolean;
  hasDesignatedRep: boolean;
}) {
  return (
    <CollapsiblePane
      initialIsCollapsed={initialIsCollapsed}
      title={title}
      showIncompleteBadge={!isSectionComplete}
    >
      <div>
        {questionsWithVisibility
          .filter((q) => q.visible)
          .map((q, idx, a) => {
            return (
              <div key={q.slug} className={idx < a.length - 1 ? "border-b border-gray-200" : ""}>
                <InlineSurveyQuestionReview
                  question={q}
                  answer={answers[q.slug]}
                  isFlaggedForReview={flaggedForReviewSlugs.includes(q.slug)}
                  setAnswer={setAnswer}
                  pcSettings={pcSettings}
                  openDpoDialog={openDpoDialog}
                  openDesignatedRepDialog={openDesignatedRepDialog}
                  isDpoRequired={isDpoRequired}
                  hasDpo={hasDpo}
                  hasDesignatedRep={hasDesignatedRep}
                />
              </div>
            );
          })}
      </div>
    </CollapsiblePane>
  );
}

function CollapsiblePane({
  title,
  initialIsCollapsed = true,
  showIncompleteBadge = false,
  children,
}: {
  title: string;
  initialIsCollapsed?: boolean;
  showIncompleteBadge?: boolean;
  children: React.ReactNode;
}) {
  const [isCollapsed, setIsCollapsed] = useState(initialIsCollapsed);

  return (
    <div className={clsx("w-[1057px]", "rounded-[8px] border border-solid border-neutral-500")}>
      <div
        className={clsx(
          "bg-neutral-100 px-[24px] py-[12px] rounded-t-[8px] flex items-center justify-between",
          isCollapsed && "rounded-b-[8px]",
        )}
        onClick={() => setIsCollapsed((c) => !c)}
      >
        <div className="text-neutral-800 text-[20px] font-medium">{title}</div>
        <div className="flex items-center gap-[8px]">
          {showIncompleteBadge && <IncompleteBadge />}
          <div className="cursor-pointer p-[8px]">
            {isCollapsed ? (
              <svg
                width="8"
                height="12"
                viewBox="0 0 8 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0.578125 1.40625L1.98438 0L7.98438 6L1.98438 12L0.578125 10.5938L5.17188 6L0.578125 1.40625Z"
                  fill="#726D83"
                />
              </svg>
            ) : (
              <svg
                width="12"
                height="8"
                viewBox="0 0 12 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.5938 0.578125L12 1.98438L6 7.98438L0 1.98438L1.40625 0.578125L6 5.17188L10.5938 0.578125Z"
                  fill="#726D83"
                />
              </svg>
            )}
          </div>
        </div>
      </div>

      {!isCollapsed && children}
    </div>
  );
}

function InlineSurveyQuestionReview({
  question,
  answer,
  isFlaggedForReview,
  setAnswer,
  pcSettings,
  openDpoDialog,
  openDesignatedRepDialog,
  isDpoRequired,
  hasDpo,
  hasDesignatedRep,
}: {
  question: SurveyQuestionProps;
  answer: string;
  isFlaggedForReview: boolean;
  setAnswer: SurveyAnswerFn;
  pcSettings: PrivacyCenterSettingsProps;
  openDpoDialog: () => void;
  openDesignatedRepDialog: () => void;
  isDpoRequired: boolean;
  hasDpo: boolean;
  hasDesignatedRep: boolean;
}) {
  const [isEditing, setIsEditing] = useState(false);

  const [eeaContactDoesNotApply, setEeaContactDoesNotApply] = useState(
    pcSettings.eeaContactDoesNotApply,
  );
  const [dpoDoesNotApply, setDpoDoesNotApply] = useState(pcSettings.dpoDoesNotApply);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setEeaContactDoesNotApply(pcSettings.eeaContactDoesNotApply);
  }, [pcSettings.eeaContactDoesNotApply]);

  let questionLabel =
    question.settingsQuestionLabel ||
    question.plaintextQuestion ||
    question.question ||
    question.slug;
  // Since data Source questions already show the question text, don't show the question text  again if we're editing a data source question
  let showQuestionLabel = !(question.type === "custom-add-data-source" && isEditing);

  let isDpoQuestion = question.slug === HAS_DATA_PRIVACY_OFFICER_SLUG;
  let isDesignatedRepQuestion = question.slug === HAS_DESIGNATED_REP_SLUG;

  let isAnswered = answer !== undefined && answer !== null && answer !== "";
  if (isDpoQuestion) {
    isAnswered = hasDpo || !isDpoRequired || dpoDoesNotApply;
  }
  if (isDesignatedRepQuestion) {
    isAnswered = hasDesignatedRep || eeaContactDoesNotApply;
  }

  return (
    <div className="p-[16px] flex items-start justify-between">
      <div>
        {showQuestionLabel && (
          <>
            <div className="pr-[12px] text-[18px] font-medium text-neutral-800">
              {isEditing ? question.question : questionLabel}
            </div>
            {isEditing && question.helpText && (
              <div className="mt-[6px] text-neutral-700 text-[16px]">{question.helpText}</div>
            )}
          </>
        )}
        <div className="mt-[6px]">
          {!isDpoQuestion &&
            !isDesignatedRepQuestion &&
            (isEditing ? (
              <SurveyQuestion
                question={question}
                answer={answer}
                onAnswer={async (ans) => {
                  let questionText = question.plaintextQuestion;
                  if (!questionText && typeof question.question == "string") {
                    questionText = question.question;
                  }

                  setAnswer(
                    question.slug,
                    ans,
                    questionText,
                    /* flaggedForReview = */ isFlaggedForReview,
                  );

                  // For the DPO and Designated Rep questions, don't exit editing mode after an answer, since we also show separate buttons for editing those details
                  if (
                    question.slug !== HAS_DATA_PRIVACY_OFFICER_SLUG &&
                    question.slug !== HAS_DESIGNATED_REP_SLUG
                  ) {
                    setIsEditing(false);
                  }
                }}
                useNewDesign={true}
                onlyShowInlineAnswerButtons={true}
                showDoneAddingVendorsButton={true}
              />
            ) : isAnswered ? (
              <div className="text-[16px] text-primary-600 font-bold">
                {answerDisplayText(question, answer)}
              </div>
            ) : (
              <IncompleteBadge />
            ))}

          {isDpoQuestion && (
            <div>
              <div className="flex items-center mt-[6px]">
                {isEditing && (
                  <div className="mr-[6px]">
                    <Button color="secondary" onClick={openDpoDialog}>
                      {hasDataPrivacyOfficer(pcSettings) ? "Edit" : "Add"} DPO Details
                    </Button>
                  </div>
                )}
                {!isEditing && dpoDoesNotApply === true && (
                  <div className="text-[16px] text-primary-600 font-bold">No (Does Not Apply)</div>
                )}
                {!isEditing &&
                  isDpoRequired &&
                  !dpoDoesNotApply &&
                  !hasDataPrivacyOfficer(pcSettings) && <IncompleteBadge />}
                {hasDataPrivacyOfficer(pcSettings) && (
                  <div className="bg-green-100 inline-flex rounded-[6px] px-[12px] py-[7px] items-center self-stretch">
                    <div>
                      <CheckCircleIcon className="w-[16px h-[16px] text-green-400" />
                    </div>
                    <div className="ml-[12px] text-green-800 font-medium text-[12px] leading-4">
                      {pcSettings.dpoName}, {pcSettings.dpoEmail}
                    </div>
                  </div>
                )}
              </div>
              {isEditing && (
                <div className={clsx("mt-[12px]", isSaving && "opacity-50 cursor-not-allowed")}>
                  <SimpleCheckboxControlled
                    name="dpo-does-not-apply"
                    label="I do not require a Data Privacy Officer"
                    checked={dpoDoesNotApply}
                    onChange={async (newChecked: boolean) => {
                      setIsSaving(true);
                      await pcSettings.handleFormSubmit({
                        dpoDoesNotApply: newChecked,
                      });
                      setDpoDoesNotApply(newChecked);
                      setIsSaving(false);
                    }}
                    disabled={isSaving}
                  />
                </div>
              )}
            </div>
          )}

          {isDesignatedRepQuestion && (
            <div>
              <div className="flex items-center mt-[6px]">
                {isEditing && (
                  <div className="mr-[6px]">
                    <Button color="secondary" onClick={openDesignatedRepDialog}>
                      {hasDesignatedRepresentative(pcSettings) ? "Edit" : "Add"} Designated Rep
                      Details
                    </Button>
                  </div>
                )}
                {!isEditing && eeaContactDoesNotApply === true && (
                  <div className="text-[16px] text-primary-600 font-bold">No (Does Not Apply)</div>
                )}
                {!isEditing &&
                  !eeaContactDoesNotApply &&
                  !hasDesignatedRepresentative(pcSettings) && <IncompleteBadge />}
                {hasDesignatedRepresentative(pcSettings) && (
                  <div className="bg-green-100 inline-flex rounded-[6px] px-[12px] py-[7px] items-center self-stretch">
                    <div>
                      <CheckCircleIcon className="w-[16px h-[16px] text-green-400" />
                    </div>
                    <div className="ml-[12px] text-green-800 font-medium text-[12px] leading-4">
                      {pcSettings.eeaContactName}, {pcSettings.eeaContactEmail},{" "}
                      {pcSettings.eeaContactAddress}
                    </div>
                  </div>
                )}
              </div>
              {isEditing && (
                <div className={clsx("mt-[12px]", isSaving && "opacity-50 cursor-not-allowed")}>
                  <SimpleCheckboxControlled
                    name="eea-contact-does-not-apply"
                    label="I have read the requirements and article about Designated Representatives and this requirement does not apply to my organization."
                    checked={eeaContactDoesNotApply}
                    onChange={async (newChecked: boolean) => {
                      setIsSaving(true);
                      await pcSettings.handleFormSubmit({
                        eeaContactDoesNotApply: newChecked,
                      });
                      setEeaContactDoesNotApply(newChecked);
                      setIsSaving(false);
                    }}
                    disabled={isSaving}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center">
        <div
          className="text-[14px] font-medium text-neutral-600 cursor-pointer underline"
          onClick={() => setIsEditing((s) => !s)}
        >
          {isEditing ? "Cancel" : "Edit"}
        </div>
        <div
          className="ml-[12px] cursor-pointer"
          onClick={() => {
            let questionText = question.plaintextQuestion;
            if (!questionText && typeof question.question == "string") {
              questionText = question.question;
            }
            setAnswer(question.slug, answer, questionText, !isFlaggedForReview);
          }}
        >
          {isFlaggedForReview ? (
            <FlagIconFilled className="w-[24px] h-[24px] text-primary-500" />
          ) : (
            <FlagIcon className="w-[24px] h-[24px] text-primary-500" />
          )}
        </div>
      </div>
    </div>
  );
}

function IncompleteBadge() {
  return (
    <div
      className={clsx(
        "bg-yellow-300 rounded-[100px] px-[8px] py-[4px] inline-flex items-center justify-center",
        "text-[rgba(0,0,0,0.87)] font-medium text-[14px]",
      )}
    >
      Incomplete
    </div>
  );
}

function answerDisplayText(question: SurveyQuestionProps, answer: string) {
  if (question.type === "boolean") {
    return answer === "true" ? "Yes" : "No";
  }

  if (question.type === "custom-add-data-source") {
    let filteredVendors = filterVendorsForDataSourceSurveyQuestion(question);
    let filteredVendorIds = new Set(filteredVendors.map((v) => v.id));
    return question.sources
      .filter((s) => filteredVendorIds.has(s.vendorId))
      .map((s) => s.name)
      .join(", ");
  }

  if (question.type === "checkboxes") {
    let parsedAnswer = JSON.parse(answer || "[]");
    return question.options
      .filter((o) => parsedAnswer.includes(o.key))
      .map((o) => o.label)
      .join(", ");
  }

  return answer;
}
